/**
 * matchScoring.ts — the Roamer Match Score™ engine.
 *
 * Every recommendation gets a 0–100 match against THIS user's profile, built
 * from weighted, per-category criteria. Every score is explainable: the top
 * satisfied criteria become the "Why?" reasons; poorly-satisfied ones surface
 * as concerns. Unknown attributes are excluded (and lower the confidence)
 * rather than silently counted as neutral.
 *
 * Pure functions only — deterministic, no I/O, no React.
 */

import type {
  MatchReason,
  MatchScore,
  Recommendation,
  RecommendationAttributes,
  RecommendationCategory,
} from '../types';
import type { RoamerProfile } from '../RoamerProfile';
import {
  MAX_CONCERNS,
  MAX_REASONS,
  REASON_CONCERN_THRESHOLD,
  REASON_POSITIVE_THRESHOLD,
} from './weights';

// ─── Criterion machinery ──────────────────────────────────────────────────────

interface CriterionOutcome {
  label: string;
  /** 0–1 satisfaction, or null when the attribute is unknown. */
  satisfied: number | null;
  /** Effective weight after profile modulation. */
  weight: number;
}

type Criterion = (a: RecommendationAttributes, p: RoamerProfile) => CriterionOutcome;

const clamp01 = (x: number) => Math.max(0, Math.min(1, x));

/** Linear ramp: 1 at `best`, 0 at `worst` (works in either direction). */
function ramp(value: number, best: number, worst: number): number {
  if (best === worst) return value >= best ? 1 : 0;
  return clamp01((value - worst) / (best - worst));
}

// ─── Reusable criterion factories ─────────────────────────────────────────────

const internetCriterion = (base: number): Criterion => (a, p) => {
  const target = p.preferredInternetMbps ?? 50;
  const importance = (p.remoteWorker || p.digitalNomad ? 1.4 : 1) * (p.preferredInternetMbps != null ? 1.2 : 1);
  const weight = base * importance;
  if (a.internetMbps == null) return { label: 'Fast Internet', satisfied: null, weight };
  return { label: 'Fast Internet', satisfied: clamp01(a.internetMbps / Math.max(target, 10)), weight };
};

const budgetCriterion = (base: number, which: 'housing' | 'monthly'): Criterion => (a, p) => {
  const budget = which === 'housing' ? p.housingBudgetUsd : p.monthlyBudgetUsd;
  const weight = base * (budget != null ? 1.3 : 1);
  if (a.monthlyCost == null || budget == null) return { label: 'Within Budget', satisfied: null, weight };
  const satisfied = a.monthlyCost <= budget ? 1 : clamp01(budget / a.monthlyCost);
  return { label: 'Within Budget', satisfied, weight };
};

const priceCriterion = (base: number): Criterion => (a) => {
  if (a.priceLevel == null) return { label: 'Affordable', satisfied: null, weight: base };
  return { label: 'Affordable', satisfied: clamp01(1 - ((a.priceLevel - 1) / 3) * 0.7), weight: base };
};

const ratingCriterion = (base: number): Criterion => (a) => {
  if (a.rating == null) return { label: 'Highly Rated', satisfied: null, weight: base };
  return { label: 'Highly Rated', satisfied: clamp01(a.rating / 5), weight: base };
};

const safetyCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (0.6 + (p.safetyPriority / 100) * 1.2);
  if (a.safety == null) return { label: 'Safe Area', satisfied: null, weight };
  return { label: 'Safe Area', satisfied: clamp01(a.safety / 100), weight };
};

const quietCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (p.needsQuietWorkspace ? 1.7 : 0.6);
  if (a.quietness == null) return { label: 'Quiet Area', satisfied: null, weight };
  return { label: 'Quiet Area', satisfied: clamp01(a.quietness / 100), weight };
};

const walkableCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (0.5 + (p.walkability / 100) * 1.3);
  if (a.walkabilityMeters == null) return { label: 'Walkable', satisfied: null, weight };
  return { label: 'Walkable', satisfied: ramp(a.walkabilityMeters, 600, 3200), weight };
};

const transitCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (0.5 + (p.publicTransit / 100) * 1.3);
  if (a.transitScore == null) return { label: 'Good Transit', satisfied: null, weight };
  return { label: 'Good Transit', satisfied: clamp01(a.transitScore / 100), weight };
};

const nearCoworkingCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (p.needsCoworking ? 1.6 : 0.6);
  if (a.nearCoworkingMeters == null) return { label: 'Near Coworking', satisfied: null, weight };
  return { label: 'Near Coworking', satisfied: ramp(a.nearCoworkingMeters, 300, 2500), weight };
};

const petCriterion = (base: number): Criterion => (a, p) => {
  if (!p.petFriendly) return { label: 'Pet Friendly', satisfied: null, weight: 0 };
  if (a.petFriendly == null) return { label: 'Pet Friendly', satisfied: null, weight: base * 1.5 };
  return { label: 'Pet Friendly', satisfied: a.petFriendly ? 1 : 0, weight: base * 1.5 };
};

const longStayCriterion = (base: number): Criterion => (a, p) => {
  const cares = (p.lengthOfStayDays ?? 0) >= 28;
  const weight = base * (cares ? 1.4 : 0.4);
  if (a.longStayFriendly == null) return { label: 'Long-Stay Friendly', satisfied: null, weight };
  return { label: 'Long-Stay Friendly', satisfied: a.longStayFriendly ? 1 : 0.3, weight };
};

const boolAmenityCriterion = (base: number, key: 'hasPowerOutlets' | 'hasAirConditioning' | 'open24h', label: string): Criterion =>
  (a) => {
    const v = a[key];
    if (v == null) return { label, satisfied: null, weight: base };
    return { label, satisfied: v ? 1 : 0.2, weight: base };
  };

const nightlifeCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (0.3 + (p.nightlife / 100) * 1.4);
  if (a.nightlife == null) return { label: 'Nightlife Nearby', satisfied: null, weight };
  // If the user dislikes nightlife, a loud district is a mismatch — invert.
  const wants = p.nightlife >= 50;
  const satisfied = wants ? clamp01(a.nightlife / 100) : clamp01(1 - a.nightlife / 100);
  return { label: wants ? 'Nightlife Nearby' : 'Low-Key Area', satisfied, weight };
};

const natureCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (0.3 + (p.nature / 100) * 1.4);
  if (a.natureAccess == null) return { label: 'Nature Access', satisfied: null, weight };
  return { label: 'Nature Access', satisfied: clamp01(a.natureAccess / 100), weight };
};

const beachCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (p.beach >= 60 ? 1.2 : 0.3);
  if (a.beachAccessKm == null) return { label: 'Near the Beach', satisfied: null, weight };
  return { label: 'Near the Beach', satisfied: ramp(a.beachAccessKm, 1, 40), weight };
};

const englishCriterion = (base: number): Criterion => (a, p) => {
  const weight = base * (p.preferredLanguage && p.preferredLanguage !== 'en' ? 0.5 : 1);
  if (a.englishFriendly == null) return { label: 'English Friendly', satisfied: null, weight };
  return { label: 'English Friendly', satisfied: clamp01(a.englishFriendly / 100), weight };
};

const proximityCriterion = (base: number): Criterion => (a) => {
  if (a.distanceKm == null) return { label: 'Close to Base', satisfied: null, weight: base };
  return { label: 'Close to Base', satisfied: ramp(a.distanceKm, 0.5, 12), weight: base };
};

// ─── Per-category criteria tables ─────────────────────────────────────────────

const DEFAULT_CRITERIA: Criterion[] = [
  ratingCriterion(1.0),
  proximityCriterion(0.8),
  safetyCriterion(0.7),
];

const CATEGORY_CRITERIA: Record<RecommendationCategory, Criterion[]> = {
  housing: [
    budgetCriterion(1.4, 'housing'),
    internetCriterion(1.2),
    safetyCriterion(1.0),
    quietCriterion(0.8),
    walkableCriterion(0.8),
    nearCoworkingCriterion(0.7),
    longStayCriterion(0.7),
    petCriterion(0.7),
    ratingCriterion(0.8),
  ],
  coworking: [
    internetCriterion(1.4),
    budgetCriterion(1.0, 'monthly'),
    boolAmenityCriterion(0.7, 'open24h', '24-Hour Access'),
    quietCriterion(0.8),
    walkableCriterion(0.8),
    ratingCriterion(0.9),
    proximityCriterion(0.6),
  ],
  cafe: [
    internetCriterion(1.2),
    quietCriterion(1.0),
    boolAmenityCriterion(0.8, 'hasPowerOutlets', 'Power Outlets'),
    boolAmenityCriterion(0.6, 'hasAirConditioning', 'Air Conditioning'),
    priceCriterion(0.7),
    ratingCriterion(0.9),
    longStayCriterion(0.5),
    proximityCriterion(0.7),
  ],
  restaurant: [
    ratingCriterion(1.2),
    priceCriterion(1.0),
    proximityCriterion(0.8),
    englishCriterion(0.5),
    petCriterion(0.4),
  ],
  neighborhood: [
    safetyCriterion(1.2),
    walkableCriterion(1.1),
    internetCriterion(0.9),
    nightlifeCriterion(0.8),
    natureCriterion(0.7),
    beachCriterion(0.6),
    transitCriterion(0.8),
    budgetCriterion(0.9, 'housing'),
    englishCriterion(0.5),
  ],
  transportation: [
    transitCriterion(1.3),
    priceCriterion(0.9),
    ratingCriterion(0.8),
    proximityCriterion(0.7),
  ],
  utility: [
    ratingCriterion(1.0),
    priceCriterion(0.9),
    proximityCriterion(0.8),
    englishCriterion(0.6),
  ],
  emergency: [
    proximityCriterion(1.3),
    ratingCriterion(0.9),
    englishCriterion(0.9),
    boolAmenityCriterion(0.8, 'open24h', 'Open 24 Hours'),
  ],
  community: [
    ratingCriterion(1.0),
    englishCriterion(0.8),
    proximityCriterion(0.8),
    nightlifeCriterion(0.4),
  ],
};

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * Calculate the Roamer Match Score™ for one recommendation against a profile.
 * Deterministic: identical inputs always produce identical outputs.
 */
export function calculateMatchScore(rec: Recommendation, profile: RoamerProfile): MatchScore {
  const criteria = CATEGORY_CRITERIA[rec.category] ?? DEFAULT_CRITERIA;

  let totalWeight = 0;
  let knownWeight = 0;
  let weightedSum = 0;
  const reasons: MatchReason[] = [];
  const concerns: MatchReason[] = [];

  for (const criterion of criteria) {
    const o = criterion(rec.attributes, profile);
    if (o.weight <= 0) continue;
    totalWeight += o.weight;
    if (o.satisfied == null) continue;
    knownWeight += o.weight;
    weightedSum += o.satisfied * o.weight;
    if (o.satisfied >= REASON_POSITIVE_THRESHOLD) {
      reasons.push({ label: o.label, polarity: 'positive', weight: o.weight });
    } else if (o.satisfied <= REASON_CONCERN_THRESHOLD) {
      concerns.push({ label: o.label, polarity: 'concern', weight: o.weight });
    }
  }

  const score = knownWeight > 0 ? Math.round((weightedSum / knownWeight) * 100) : 50;
  const confidence = totalWeight > 0 ? Math.round((knownWeight / totalWeight) * 100) : 0;

  reasons.sort((x, y) => y.weight - x.weight);
  concerns.sort((x, y) => y.weight - x.weight);

  return {
    score: Math.max(0, Math.min(100, score)),
    reasons: reasons.slice(0, MAX_REASONS),
    concerns: concerns.slice(0, MAX_CONCERNS),
    confidence,
  };
}
